import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Chip, Avatar, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import { 
  DataGrid,
} from '@mui/x-data-grid';
import Headeer from '../../components/Headeer';
import GradientHeaderIconButton from '../../components/GradientHeaderIconButton';
import { tokens } from '../../theme';
import { useTheme } from '@emotion/react';
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BarChartIcon from "@mui/icons-material/BarChart";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import { useNavigate } from 'react-router-dom';
import PatientService from '../../services/patientService';
import CollapsibleListFilters, {
  ListFiltersExcelButton,
  listFiltersSelectProps,
} from '../../components/CollapsibleListFilters';

export default function Patients() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('patientsPage');
  const patientService = useMemo(() => new PatientService(), []);
  const navigate = useNavigate()
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
      page: 0,
      pageSize: 10,
    });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    patientName: "",
    email: "",
    doctorName: "",
    gender: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [exporting, setExporting] = useState(false);
  const clickToAddPatients = () => {
    navigate("/AddPatient");
  };

  const buildCleanFilters = (source) => {
    const next = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        next[key] = String(value).trim().toLowerCase();
      }
    });
    return next;
  };

  useEffect(() => {
    const fetchAllPatients = async () => {
      try {
        setLoading(true);
        const response = await patientService.getAllPatients({
          withMeta: true,
          params: appliedFilters,
          headers: {
            "x-bone-pageindex": paginationModel.page,
            "x-bone-limit": paginationModel.pageSize,
            "x-bone-sortby": "createdAt",
            "x-bone-sortorder": "DESC",
          },
        });
        const formattedPatients = (response?.data?.patients || []).map(patient => ({
          ...patient,
          doctorName: patient.Doctor ? `${patient.Doctor.name} ${patient.Doctor.surname}` : t('notAvailable')
        }));
        setPatients(formattedPatients);
        setTotalCount(Number(response?.headers?.["x-bone-totalcount"]) || 0);
      } catch (error) {
        console.log(error);
        setPatients([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchAllPatients();
  }, [patientService, appliedFilters, paginationModel.page, paginationModel.pageSize, t]);
  
  const columns = [
    {
      field: 'patient',
      headerName: t('patient'),
      flex: 1.5,
      minWidth: 200,
      valueGetter: (params) => {
        const firstName = params.row.firstName || '';
        const lastName = params.row.lastName || '';
        return `${firstName} ${lastName}`.trim();
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              fontWeight: 700,
              fontSize: '0.85rem'
            }}
          >
            {params.row.firstName?.charAt(0)}{params.row.lastName?.charAt(0)}
          </Avatar>
          <Box>
            <Box sx={{ fontWeight: 600, color: colors.grey[100] }}>
              {params.row.firstName} {params.row.lastName}
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[400] }}>
              {t('idLabel')} {params.row.id}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: t('email'),
      flex: 1.2,
      minWidth: 200,
      valueGetter: (params) => params.row.email || '',
    },
    {
      field: 'primaryPhone',
      headerName: t('primaryPhone'),
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.primaryPhone || params.row.phone_number || '',
    },
    {
      field: 'dateOfBirth',
      headerName: t('dateOfBirth'),
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => {
        if (!params.row.dateOfBirth && !params.row.birthDate) return '';
        const dateValue = params.row.dateOfBirth || params.row.birthDate;
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      },
      renderCell: (params) => {
        if (!params.value) return '-';
        return params.value;
      }
    },
    {
      field: 'gender',
      headerName: t('gender'),
      flex: 0.8,
      minWidth: 120,
      valueGetter: (params) => params.row.gender || '',
      renderCell: (params) => {
        const gender = params.value?.toLowerCase();
        return (
          <Chip
            icon={gender === 'male' ? <MaleIcon /> : <FemaleIcon />}
            label={params.value}
            size="small"
            sx={{
              backgroundColor: gender === 'male' 
                ? `${colors.blueAccent[700]}40` 
                : `${colors.greenAccent[700]}40`,
              color: gender === 'male' 
                ? colors.blueAccent[300] 
                : colors.greenAccent[300],
              fontWeight: 600,
              border: `1px solid ${gender === 'male' 
                ? colors.blueAccent[500] 
                : colors.greenAccent[500]}40`,
            }}
          />
        );
      }
    },
    {
      field: 'doctorName',
      headerName: t('doctor'),
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        if (params.row.doctorName) return params.row.doctorName;
        if (params.row.Doctor) {
          const doctor = params.row.Doctor;
          return `${doctor.name || ''} ${doctor.surname || ''}`.trim();
        }
        return '';
      },
      renderCell: (params) => (
        <Chip
          label={params.value || t('notAvailable')}
          size="small"
          sx={{
            backgroundColor: `${colors.grey[700]}40`,
            color: colors.grey[200],
            fontWeight: 500,
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={t('viewDetails')}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/patients/${params.id}`);
            }}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              border: `1px solid ${colors.blueAccent[500]}40`,
              '&:hover': {
                backgroundColor: `${colors.blueAccent[600]}60`,
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const activeFilterCount = Object.keys(appliedFilters).length;
  const handleExcelExport = async () => {
    try {
      setExporting(true);
      const response = await patientService.exportPatientsExcel(appliedFilters);
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `patients-${Date.now()}.xlsx`;
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Patient excel export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Headeer title={t('title')} subtitle={t('subtitle')} />
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <GradientHeaderIconButton
            onClick={() => navigate('/patients/statistics')}
            variant="statistics"
            aria-label={t('viewStatistics')}
            icon={<BarChartIcon sx={{ mr: 1 }} />}
          >
            {t('viewStatistics')}
          </GradientHeaderIconButton>
          <GradientHeaderIconButton
            onClick={clickToAddPatients}
            variant="primary"
            aria-label={t('addPatient')}
            icon={<PersonAddIcon sx={{ mr: 1 }} />}
          >
            {t('addPatient')}
          </GradientHeaderIconButton>
        </Box>
      </Box>

      <CollapsibleListFilters
        colors={colors}
        open={filtersOpen}
        onToggle={() => setFiltersOpen((prev) => !prev)}
        activeFilterCount={activeFilterCount}
        onApply={() => {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters(buildCleanFilters(filters));
        }}
        onClear={() => {
          setFilters({ patientName: "", email: "", doctorName: "", gender: "", dateFrom: "", dateTo: "" });
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters({});
        }}
        exportAction={
          <ListFiltersExcelButton
            colors={colors}
            onClick={handleExcelExport}
            disabled={exporting}
            loading={exporting}
          />
        }
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Patient Name"
              value={filters.patientName}
              onChange={(e) => setFilters((prev) => ({ ...prev, patientName: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              value={filters.email}
              onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Doctor Name"
              value={filters.doctorName}
              onChange={(e) => setFilters((prev) => ({ ...prev, doctorName: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Gender"
              value={filters.gender}
              onChange={(e) => setFilters((prev) => ({ ...prev, gender: e.target.value }))}
              SelectProps={listFiltersSelectProps(colors)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </CollapsibleListFilters>

      {/* DataGrid Card */}
      <Card
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          borderRadius: '16px',
          border: `1px solid ${colors.grey[800]}30`,
          boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${colors.blueAccent[700]}60`,
              borderBottom: `2px solid ${colors.blueAccent[500]}40`,
              borderRadius: 0,
              fontWeight: 700,
              fontSize: '0.95rem',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${colors.grey[800]}20`,
              fontSize: '0.9rem',
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: `${colors.blueAccent[800]}20`,
                cursor: 'pointer',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: `${colors.blueAccent[700]}40`,
              borderTop: `2px solid ${colors.blueAccent[500]}40`,
              borderRadius: 0,
            },
            '& .MuiTablePagination-root': {
              color: colors.grey[100],
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <DataGrid
            rows={patients}
            columns={columns}
            loading={loading}
            autoHeight
            pagination
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: `${colors.primary[500]}20`,
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
}
